import { useCallback, useEffect, useState } from 'react';
import { INPUT_TYPES } from '__components/Kizen/Input';
import TextInput from '__components/Inputs/TextInput';
import { OutlineInputControl } from '__components/Inputs/InputControl';
import { useDebounce } from 'react-use';
import { DEFAULT_INPUT_DELAY } from 'utility/config';
import { useTranslation } from 'react-i18next';
import { useSetupAssistant } from '@kizenapps/engine/react';
import { AssistantField } from '@kizenapps/engine';
import { Typography } from '@kizen/kds/Typography';
import { Link } from 'react-router-dom';
import { INTEGRATION_SECRETS_ROUTE } from '__pages/Settings/pages/ManageConnections/subroutes';
import { useMarketplacePermissions } from '__pages/Marketplace/permissions';
import styled from '@emotion/styled';

const Note = styled(Typography)`
  display: block;
  margin: 10px 0 0 0;
  color: var(--color-font-secondary);
  font-size: 16px;
  font-style: italic;
`;

const NoteLink = styled(Link)`
  color: var(--color-button-secondary-default);
`;

const ReadOnlyExplanation = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  border: solid 1px var(--color-border-medium);
  border-radius: 4px;
  background: var(--color-background-light);
  font-family: 'Proxima Nova', sans-serif;
  font-size: 14px;
  font-style: italic;
  color: var(--color-font-secondary);
`;

export const ApiKeyBlock = ({
  field,
  disabled = false,
}: {
  field: AssistantField;
  disabled?: boolean;
}) => {
  const {
    state,
    setState,
    evaluateExpression,
    shouldHideField,
    getFieldErrorState,
    registerFieldResetter,
    afterFieldChange,
    disabledKeys,
  } = useSetupAssistant();

  const { canEditIntegrationSecrets } = useMarketplacePermissions();

  const stored = (state as any)[field.key] as
    | { value?: string; hasValue?: boolean; maskedValue?: string }
    | undefined;
  const hasValue = Boolean(stored?.hasValue);

  const [internalValue, setInternalValue] = useState(stored?.value ?? '');

  const { t } = useTranslation();

  const isDisabled = disabledKeys.includes(field.key) || disabled;

  const setValue = useCallback(
    (newValue: string) => {
      setState((prevState: any) => ({
        ...prevState,
        [field.key]: { type: 'api_key', value: newValue },
      }));

      afterFieldChange(field.key);
    },
    [field.key, setState, afterFieldChange]
  );

  useEffect(() => {
    registerFieldResetter(field.key, () => {
      setInternalValue('');
      setState((prevState: any) => {
        const next = { ...prevState };

        delete next[field.key];

        return next;
      });

      afterFieldChange(field.key);
    });
  }, [field.key, registerFieldResetter, setState, afterFieldChange]);

  useEffect(() => {
    if (field.when) {
      evaluateExpression(field.when, field.key);
    }
  }, [field.when, evaluateExpression, field.key]);

  useDebounce(
    () => {
      if (!hasValue) {
        setValue(internalValue);
      }
    },
    DEFAULT_INPUT_DELAY,
    [internalValue, hasValue]
  );

  if (shouldHideField(field.key)) {
    return null;
  }

  const errorState = getFieldErrorState(field.key);

  if (hasValue) {
    return (
      <div>
        <TextInput
          value={stored?.maskedValue ?? '••••••••••••'}
          label={field.label}
          labelInfo={field.tooltip}
          required={field.required}
          inModal
          disabled
        />
        <Note>
          {t('This key can be managed in the {{link}} section of Settings.', {
            link: '__LINK__',
          })
            .split('__LINK__')
            .flatMap((part, i) =>
              i === 0
                ? [part]
                : [
                    <NoteLink
                      key="link"
                      to={INTEGRATION_SECRETS_ROUTE}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('Integration Secrets')}
                    </NoteLink>,
                    part,
                  ]
            )}
        </Note>
      </div>
    );
  }

  if (!canEditIntegrationSecrets) {
    return (
      <OutlineInputControl
        label={field.label}
        labelInfo={field.tooltip}
        isShowAsterisk={field.required}
      >
        <ReadOnlyExplanation>
          {t(
            "You don't have permission to set this — ask an administrator to add it in Integration Secrets."
          )}
        </ReadOnlyExplanation>
      </OutlineInputControl>
    );
  }

  const errorProps = {
    error: errorState?.message,
    validate: {
      message: errorState?.message,
      showMessage: errorState?.showMessage,
    },
  };

  return (
    <TextInput
      placeholder={field.placeholder ?? t('Enter API key')}
      value={internalValue}
      onChange={setInternalValue}
      onBlur={() => setValue(internalValue)}
      label={field.label}
      labelInfo={field.tooltip}
      inModal
      type={INPUT_TYPES.PASSWORD}
      {...errorProps}
      disabled={isDisabled}
      required={field.required}
    />
  );
};
